import { Text, Tooltip } from '@chakra-ui/react';
import WarningSharpIcon from '@gamma/ui/icons/WarningSharpIcon';
import { SidebarContext, SidebarFooterItem } from '@gamma/ui/navigation';
import moment from 'moment';
import { useContext, useMemo } from 'react';
import { ROUTES } from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers/AuthProvider';
import { expirationDatesLicenseCompare } from '@investigator/shared/utils';

const { systemSettingsGeneral } = ROUTES;
interface WarningContentProps {
  daysLeft?: number;
  timestamp: number;
  expired: boolean;
}

export const WarningContent = ({ timestamp, expired }: WarningContentProps) => {
  const { licenseExpiring } = i18n.navigation;
  const format = 'll';
  const time = moment.unix(timestamp);
  const formattedTime = time.format(format);

  if (expired) {
    return (
      <Text noOfLines={1} fontSize="sm">
        {licenseExpiring.descExpired}
      </Text>
    );
  }

  return (
    <Text noOfLines={2} fontSize="sm">
      {i18n.formatString(licenseExpiring.descExpiresOn, <b>{formattedTime}</b>)}
    </Text>
  );
};

export const LicenseExpireWarning = () => {
  const { userLicense, userRole } = useContext(AuthContext);
  const { isOpen } = useContext(SidebarContext);
  const timestamp = userLicense?.end || 0;
  const userLicenseType = userLicense?.license_type;
  const expired = !!userLicense?.expired;
  const { licenseExpiring } = i18n.navigation;

  const expirationMeta = useMemo(
    () => expirationDatesLicenseCompare(timestamp, userLicenseType, expired),
    [timestamp, expired, userLicenseType],
  );

  const statusCheck = expirationMeta.status === 'success';

  if (userRole !== 'admin' || statusCheck) {
    return null;
  }

  return (
    <Tooltip
      label={licenseExpiring.descViewInfo}
      shouldWrapChildren
      isDisabled={!isOpen} // since there's already a tooltip when closed, disable it
    >
      <SidebarFooterItem
        icon={<WarningSharpIcon color={`${expirationMeta.color}.300`} />}
        href={systemSettingsGeneral}
        target="_self"
        data-testid={`license-${expired ? 'expired' : 'expiring'}-warning`}
      >
        <WarningContent
          daysLeft={expirationMeta.daysLeft}
          expired={expired}
          timestamp={timestamp}
        />
      </SidebarFooterItem>
    </Tooltip>
  );
};
